import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getRoles, deleteRole } from '../api';
import {
  Button,
  ConfirmDeleteModal,
  SearchInput,
  SkeletonText,
} from '@/components';
import { V2Table } from '@/common/components';
import { parseApiError } from '@/common/utils';
import { PlusIcon } from '../components/Icons';
import editIcon from '../assets/editIcon.png';
import trashIcon from '../assets/trashIcon.png';
import { usePermissions } from '@/common/hooks/usePermissions';
import { PERMISSIONS } from '@/common/config/permissions';

import { RoleModal } from './RoleModal';
import { RoleDetailsModal } from './RoleDetailsModal';
export function RolesTab() {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editingRole, setEditingRole] = useState(null);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [viewingRole, setViewingRole] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const { can } = usePermissions();
  const hasAnyAction = true; // We always show 'View' action

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const getCompanyId = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        return userObj?.company_id || localStorage.getItem('company_id');
      }
    } catch (e) {
      console.error('Failed to parse user object from localStorage', e);
    }
    return localStorage.getItem('company_id');
  };

  const companyId = getCompanyId();

  const { data, isLoading } = useQuery({
    queryKey: ['companyRoles', companyId, debouncedSearch, page],
    queryFn: () => {
      const params = { page };
      if (debouncedSearch) params.search = debouncedSearch;
      return getRoles(companyId, params);
    },
    enabled: !!companyId && can(PERMISSIONS.ROLES.VIEW),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => {
      if (!can(PERMISSIONS.ROLES.DELETE))
        throw new Error(
          'Permission denied: You do not have access to delete roles.',
        );
      return deleteRole({ companyId, roleId: id });
    },
    onSuccess: () => {
      toast.success('Role deleted successfully!');
      setRoleToDelete(null);
      queryClient.invalidateQueries(['companyRoles', companyId]);
    },
    onError: (err) => {
      if (err?.response?.status >= 500) {
        toast.error('Internal Server Error. Please try again later.');
        return;
      }
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to delete role');
    },
  });

  const openEditModal = (role) => {
    setEditingRole(role);
    setIsModalOpen(true);
  };

  let roles = Array.isArray(data) ? data : data?.results || [];
  const rolesTotal = data?.count || (Array.isArray(data) ? data.length : 0);

  if (debouncedSearch && Array.isArray(data)) {
    const lowerSearch = debouncedSearch.toLowerCase();
    roles = roles.filter((r) => r.name?.toLowerCase().includes(lowerSearch));
  }

  const roleColumns = [
    {
      id: 'name',
      label: 'Role Name',
      defaultWidth: 200,
      minWidth: 140,
      resizable: true,
      skeleton: <SkeletonText width="w-32" height="h-4" />,
      cell: (role) => (
        <span className="truncate block" title={role.name}>
          {role.name}
        </span>
      ),
    },
    {
      id: 'description',
      label: 'Description',
      defaultWidth: 280,
      minWidth: 180,
      resizable: true,
      skeleton: <SkeletonText width="w-40" height="h-4" />,
      cell: (role) => (
        <span className="truncate block" title={role.description || '-'}>
          {role.description || '-'}
        </span>
      ),
    },
    {
      id: 'type',
      label: 'Type',
      defaultWidth: 120,
      minWidth: 100,
      resizable: false,
      align: 'center',
      skeleton: <SkeletonText width="w-16" height="h-4" className="mx-auto" />,
      cell: (role) =>
        role.is_system ? (
          <span className="px-2 py-1 text-xs font-semibold rounded-md bg-blue-500/10 text-blue-400 border border-blue-500/20">
            System
          </span>
        ) : (
          <span className="px-2 py-1 text-xs font-semibold rounded-md bg-zinc-500/10 text-zinc-400 border border-zinc-500/20">
            Custom
          </span>
        ),
    },
    {
      id: 'members',
      label: 'Assigned Members',
      defaultWidth: 160,
      minWidth: 130,
      resizable: true,
      align: 'center',
      skeleton: <SkeletonText width="w-12" height="h-4" className="mx-auto" />,
      cell: (role) => role.assigned_members_count || 0,
    },
  ];

  if (hasAnyAction) {
    roleColumns.push({
      id: 'actions',
      label: 'Actions',
      defaultWidth: 110,
      minWidth: 90,
      resizable: false,
      align: 'center',
      stopPropagation: true,
      skeleton: <SkeletonText width="w-16" height="h-4" className="mx-auto" />,
      cell: (role) => (
        <div className="flex items-center justify-center gap-4">
          {can(PERMISSIONS.ROLES.UPDATE) && (
            <button
              type="button"
              onClick={() => openEditModal(role)}
              className="text-text-muted hover:text-text-primary transition-colors shrink-0 disabled:opacity-40"
              title="Edit"
              disabled={role.is_system}
            >
              <img
                src={editIcon}
                alt=""
                className="w-5 h-5 object-contain shrink-0"
              />
            </button>
          )}
          {can(PERMISSIONS.ROLES.DELETE) && (
            <button
              type="button"
              onClick={() => setRoleToDelete(role)}
              className="text-red-500 hover:text-red-400 transition-colors shrink-0 disabled:opacity-40"
              title="Delete"
              disabled={
                (deleteMutation.isPending && roleToDelete?.id === role.id) ||
                role.is_system
              }
            >
              <img
                src={trashIcon}
                alt=""
                className="w-5 h-5 object-contain shrink-0"
              />
            </button>
          )}
        </div>
      ),
    });
  }

  return (
    <div className="flex flex-col gap-2 animate-in fade-in duration-300 flex-grow flex-1 min-h-0 overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full shrink-0 py-[10px]">
        <SearchInput
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by Role Name"
          className="w-full sm:max-w-md sm:flex-1"
          inputClassName="h-[44px]"
        />
        {can(PERMISSIONS.ROLES.CREATE) && (
          <Button
            variant="primary"
            onClick={() => {
              setEditingRole(null);
              setIsModalOpen(true);
            }}
            className="h-[44px] px-4 w-full sm:w-auto shrink-0 sm:ml-auto"
            icon={<PlusIcon />}
          >
            <span>Create Role</span>
          </Button>
        )}
      </div>

      <div className="w-full min-w-0 flex-1 min-h-0 flex flex-col">
        <V2Table
          tight
          columns={roleColumns}
          data={roles}
          rowKey="id"
          storageKey="roles_table_column_widths"
          loading={isLoading}
          loadingRowCount={5}
          onRowClick={(role) => {
            setViewingRole(role);
            setIsViewModalOpen(true);
          }}
          empty={
            <div className="flex flex-col items-center justify-center gap-4">
              <div className="w-16 h-16 bg-input-background border border-border-default rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 opacity-50"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              </div>
              <span className="text-base font-medium">No roles found</span>
            </div>
          }
          pagination={{
            currentPage: page,
            totalCount: rolesTotal,
            itemsPerPage: 10,
            onChange: setPage,
          }}
        />
      </div>

      <RoleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        editingRole={editingRole}
        companyId={companyId}
      />

      <RoleDetailsModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        roleId={viewingRole?.id}
        roleName={viewingRole?.name}
        companyId={companyId}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={!!roleToDelete}
        title="Delete Role"
        description="This role will be permanently deleted. Ensure no members are actively using it."
        onConfirm={() => deleteMutation.mutate(roleToDelete.id)}
        onCancel={() => !deleteMutation.isPending && setRoleToDelete(null)}
        isPending={deleteMutation.isPending}
      />
    </div>
  );
}
